(function () {
  var SELECTOR = '[data-blng-themed-bg-enabled]';
  var SECTION_CLASS = 'blng-themed-bg-enabled';
  var imageCache = new Map();
  var parallaxSections = new Set();
  var rafId = null;

  function toBool(value, fallback) {
    if (typeof value === 'boolean') return value;
    if (typeof value !== 'string') return fallback;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  }

  function toNumber(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function normalizeCharset(charset) {
    var normalized = String(charset || '').replace(/\s+/g, ' ');
    if (normalized.length < 2) return ' .:-=+*#%@';
    return normalized;
  }

  function getDirectLayer(section) {
    for (var i = 0; i < section.children.length; i += 1) {
      var child = section.children[i];
      if (child.classList && child.classList.contains('blng-themed-bg-layer')) {
        return child;
      }
    }
    return null;
  }

  function createLayer(section) {
    var existing = getDirectLayer(section);
    if (existing) return existing;

    var layer = document.createElement('div');
    layer.className = 'blng-themed-bg-layer';
    layer.setAttribute('aria-hidden', 'true');

    var fancy = document.createElement('div');
    fancy.className = 'blng-themed-bg-fancy';
    fancy.appendChild(document.createElement('span'));
    fancy.appendChild(document.createElement('span'));
    fancy.appendChild(document.createElement('span'));

    var ascii = document.createElement('pre');
    ascii.className = 'blng-themed-bg-ascii';
    ascii.setAttribute('aria-hidden', 'true');

    var noise = document.createElement('div');
    noise.className = 'blng-themed-bg-noise';

    layer.appendChild(fancy);
    layer.appendChild(ascii);
    layer.appendChild(noise);

    if (section.firstChild) {
      section.insertBefore(layer, section.firstChild);
    } else {
      section.appendChild(layer);
    }
    return layer;
  }

  function destroyLayer(section) {
    var layer = getDirectLayer(section);
    if (layer) layer.remove();
    section.classList.remove(SECTION_CLASS);
    section.style.removeProperty('--blng-themed-bg-scroll');
    parallaxSections.delete(section);
  }

  function applyVars(section) {
    var opacity = clamp(toNumber(section.dataset.blngThemedBgOpacity, 35), 0, 100) / 100;
    var speed = clamp(toNumber(section.dataset.blngThemedBgSpeed, 100), 20, 300) / 100;
    var blur = clamp(toNumber(section.dataset.blngThemedBgBlur, 0), 0, 40);
    var scale = clamp(toNumber(section.dataset.blngThemedBgScale, 100), 80, 170) / 100;
    var tintStrength = clamp(toNumber(section.dataset.blngThemedBgTintStrength, 35), 0, 100) / 100;
    var asciiDensity = clamp(toNumber(section.dataset.blngThemedBgAsciiDensity, 8), 4, 16);
    var asciiFontSize = clamp(18 - asciiDensity, 8, 14);
    var asciiTextScale = clamp(toNumber(section.dataset.blngThemedBgAsciiTextScale, 100), 70, 160) / 100;

    section.style.setProperty('--blng-themed-bg-opacity', String(opacity));
    section.style.setProperty('--blng-themed-bg-speed', String(speed));
    section.style.setProperty('--blng-themed-bg-blur', blur + 'px');
    section.style.setProperty('--blng-themed-bg-scale', String(scale));
    section.style.setProperty('--blng-themed-bg-tint', section.dataset.blngThemedBgTint || '#22d3ee');
    section.style.setProperty('--blng-themed-bg-tint-strength', String(tintStrength));
    section.style.setProperty('--blng-themed-bg-blend-mode', section.dataset.blngThemedBgBlendMode || 'normal');
    section.style.setProperty('--blng-themed-bg-ascii-fg', section.dataset.blngThemedBgAsciiFg || '#e2e8f0');
    section.style.setProperty('--blng-themed-bg-ascii-bg', section.dataset.blngThemedBgAsciiBg || '#020617');
    section.style.setProperty('--blng-themed-bg-ascii-font-size', asciiFontSize + 'px');
    section.style.setProperty('--blng-themed-bg-ascii-text-scale', String(asciiTextScale));
  }

  function drawTextToCanvas(canvas, text, fg, bg) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return false;
    var lines = String(text || 'BLNG')
      .split('\n')
      .map(function (line) {
        return line.trim();
      })
      .filter(Boolean);
    if (!lines.length) lines = ['BLNG'];

    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    var maxWidth = canvas.width * 0.86;
    var baseSize = Math.floor(canvas.height / Math.max(lines.length, 1) * 0.72);
    var fontSize = clamp(baseSize, 36, 150);
    ctx.font = '700 ' + fontSize + 'px SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace';
    ctx.fillStyle = fg;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (var i = 0; i < lines.length; i += 1) {
      while (ctx.measureText(lines[i]).width > maxWidth && fontSize > 20) {
        fontSize -= 2;
        ctx.font = '700 ' + fontSize + 'px SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace';
      }
    }

    var lineHeight = fontSize * 1.2;
    var startY = canvas.height / 2 - ((lines.length - 1) * lineHeight) / 2;

    for (var j = 0; j < lines.length; j += 1) {
      ctx.fillText(lines[j], canvas.width / 2, startY + j * lineHeight);
    }
    return true;
  }

  function loadImage(url) {
    if (!url) return Promise.reject(new Error('Missing image url'));
    if (imageCache.has(url)) return imageCache.get(url);

    var promise = new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        reject(new Error('Could not load image'));
      };
      img.src = url;
    });
    imageCache.set(url, promise);
    return promise;
  }

  function drawImageToCanvas(canvas, image, bg) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return false;
    var cw = canvas.width;
    var ch = canvas.height;
    var iw = image.naturalWidth || image.width || 1;
    var ih = image.naturalHeight || image.height || 1;
    var canvasRatio = cw / ch;
    var imageRatio = iw / ih;
    var drawWidth;
    var drawHeight;
    var dx;
    var dy;

    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, cw, ch);

    if (imageRatio > canvasRatio) {
      drawHeight = ch;
      drawWidth = ch * imageRatio;
      dx = (cw - drawWidth) / 2;
      dy = 0;
    } else {
      drawWidth = cw;
      drawHeight = cw / imageRatio;
      dx = 0;
      dy = (ch - drawHeight) / 2;
    }

    ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
    return true;
  }

  function imageDataToAscii(imageData, width, height, charset, density, invert) {
    var chars = normalizeCharset(charset);
    var px = imageData.data;
    var yStep = clamp(density, 4, 18);
    var xStep = Math.max(2, Math.floor(yStep * 0.58));
    var lines = [];
    var maxLines = 90;
    var maxCols = 260;

    for (var y = 0; y < height; y += yStep) {
      if (lines.length >= maxLines) break;
      var line = '';
      for (var x = 0; x < width; x += xStep) {
        if (line.length >= maxCols) break;
        var index = (y * width + x) * 4;
        var r = px[index];
        var g = px[index + 1];
        var b = px[index + 2];
        var luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        var level = invert ? luminance : 1 - luminance;
        var charIndex = Math.floor(level * (chars.length - 1));
        line += chars[clamp(charIndex, 0, chars.length - 1)];
      }
      lines.push(line);
    }

    return lines.join('\n');
  }

  function renderAscii(section, asciiElement) {
    if (!asciiElement) return;
    var density = clamp(toNumber(section.dataset.blngThemedBgAsciiDensity, 8), 4, 16);
    var invert = toBool(section.dataset.blngThemedBgAsciiInvert, false);
    var charset = section.dataset.blngThemedBgAsciiCharset || ' .:-=+*#%@';
    var source = section.dataset.blngThemedBgAsciiSource || 'text';
    var textInput = section.dataset.blngThemedBgAsciiInput || 'BLNG';
    var asciiImage = section.dataset.blngThemedBgAsciiImage || '';
    var fg = section.dataset.blngThemedBgAsciiFg || '#e2e8f0';
    var bg = section.dataset.blngThemedBgAsciiBg || '#020617';
    var canvas = document.createElement('canvas');
    var token = String(Date.now()) + '-' + Math.random().toString(36).slice(2);

    canvas.width = 840;
    canvas.height = 360;
    asciiElement.dataset.renderToken = token;

    function commitAscii() {
      var ctx = canvas.getContext('2d');
      if (!ctx) return;
      var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      var ascii = imageDataToAscii(imageData, canvas.width, canvas.height, charset, density, invert);
      if (asciiElement.dataset.renderToken !== token) return;
      asciiElement.textContent = ascii || 'BLNG';
    }

    if (source === 'image' && asciiImage) {
      loadImage(asciiImage)
        .then(function (img) {
          drawImageToCanvas(canvas, img, bg);
          commitAscii();
        })
        .catch(function () {
          drawTextToCanvas(canvas, textInput, fg, bg);
          commitAscii();
        });
      return;
    }

    drawTextToCanvas(canvas, textInput, fg, bg);
    commitAscii();
  }

  function syncSection(section) {
    if (!section || !section.dataset) return;
    var enabled = toBool(section.dataset.blngThemedBgEnabled, false);
    if (!enabled) {
      destroyLayer(section);
      return;
    }

    section.classList.add(SECTION_CLASS);
    applyVars(section);

    var layer = createLayer(section);
    var theme = section.dataset.blngThemedBgTheme || 'fancy_aurora';
    var animate = toBool(section.dataset.blngThemedBgAnimate, true);
    var pin = toBool(section.dataset.blngThemedBgPinToViewport, false);

    layer.dataset.theme = theme;
    layer.dataset.animate = animate ? 'true' : 'false';
    layer.dataset.pin = pin ? 'true' : 'false';

    if (pin) {
      parallaxSections.add(section);
    } else {
      parallaxSections.delete(section);
      section.style.setProperty('--blng-themed-bg-scroll', '0px');
    }

    if (theme === 'ascii') {
      renderAscii(section, layer.querySelector('.blng-themed-bg-ascii'));
    }
  }

  function refreshAll(root) {
    var context = root || document;
    var nodes = context.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i += 1) {
      syncSection(nodes[i]);
    }
    scheduleParallaxTick();
  }

  function scheduleParallaxTick() {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(function () {
      rafId = null;
      updateParallax();
    });
  }

  function updateParallax() {
    if (!parallaxSections.size) return;
    var vhHalf = window.innerHeight / 2;

    parallaxSections.forEach(function (section) {
      if (!document.body.contains(section)) {
        parallaxSections.delete(section);
        return;
      }

      var rect = section.getBoundingClientRect();
      if (rect.bottom < -120 || rect.top > window.innerHeight + 120) {
        return;
      }

      var center = rect.top + rect.height / 2;
      var delta = vhHalf - center;
      var offset = clamp(delta * 0.08, -42, 42);
      section.style.setProperty('--blng-themed-bg-scroll', offset.toFixed(2) + 'px');
    });
  }

  function init(root) {
    refreshAll(root);
  }

  window.BLNGThemedBackground = {
    refreshAll: function () {
      refreshAll(document);
    },
    refreshSection: function (sectionOrId) {
      var section = sectionOrId;
      if (typeof sectionOrId === 'string') {
        section = document.getElementById(sectionOrId);
      }
      if (!section) return;
      syncSection(section);
      scheduleParallaxTick();
    }
  };

  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) {
      refreshAll(event.target);
    } else {
      refreshAll(document);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event || !event.target || !event.target.id) return;
    var section = document.getElementById(event.target.id);
    if (section) destroyLayer(section);
  });

  document.addEventListener('shopify:section:reorder', function () {
    scheduleParallaxTick();
  });

  window.addEventListener('scroll', scheduleParallaxTick, { passive: true });
  window.addEventListener('resize', scheduleParallaxTick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
